import React from 'react';
import { Users } from 'lucide-react';
import type { WorkerFulfillmentStat } from '../../hooks/useWorkerFulfillmentStats';

const CircularProgress: React.FC<{
  value: number;
  size?: number;
  strokeWidth?: number;
  label: string;
  count: number;
}> = ({ value, size = 80, strokeWidth = 8, label, count }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const percentage = Math.min(value, 100);
  const offset = circumference - (percentage / 100) * circumference;

  const getColorClass = () => {
    if (percentage >= 80) return 'text-green-500';
    if (percentage >= 60) return 'text-blue-500';
    if (percentage >= 40) return 'text-yellow-500';
    return 'text-red-500';
  };

  return (
    <div className="flex flex-col items-center">
      <div className="relative" style={{ width: size, height: size }}>
        <svg className="transform -rotate-90" width={size} height={size}>
          <circle
            className="text-gray-200 dark:text-gray-700"
            strokeWidth={strokeWidth}
            stroke="currentColor"
            fill="transparent"
            r={radius}
            cx={size / 2}
            cy={size / 2}
          />
          <circle
            className={getColorClass()}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            stroke="currentColor"
            fill="transparent"
            r={radius}
            cx={size / 2}
            cy={size / 2}
            style={{ transition: 'stroke-dashoffset 0.5s ease-in-out' }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-lg font-bold text-gray-900 dark:text-gray-100">
            {Math.round(percentage)}%
          </span>
        </div>
      </div>
      <p className="mt-1 text-xs font-medium text-gray-600 dark:text-gray-400">{label}</p>
      <p className="text-xs text-gray-500 dark:text-gray-500">{count}</p>
    </div>
  );
};

function gradeFromPercent(fulfilledPercent: number): 'A' | 'B' | 'C' | 'D' {
  if (fulfilledPercent >= 80) return 'A';
  if (fulfilledPercent >= 60) return 'B';
  if (fulfilledPercent >= 40) return 'C';
  return 'D';
}

export interface WorkerFulfillmentSectionProps {
  stats: WorkerFulfillmentStat[];
  loading: boolean;
  error: string | null;
  title: string;
  subtitle: string;
  readyLabel: string;
  fulfilledLabel: string;
  targetLabel: string;
}

export const WorkerFulfillmentSection: React.FC<WorkerFulfillmentSectionProps> = ({
  stats,
  loading,
  error,
  title,
  subtitle,
  readyLabel,
  fulfilledLabel,
  targetLabel,
}) => {
  if (loading) {
    return (
      <div className="card p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2 flex items-center">
          <Users className="h-5 w-5 mr-2 text-blue-600" />
          {title}
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">{subtitle}</p>
        <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
          Loading…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2 flex items-center">
          <Users className="h-5 w-5 mr-2 text-blue-600" />
          {title}
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">{subtitle}</p>
        <div className="flex items-center justify-center h-32 text-red-600 dark:text-red-400">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="card p-6">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-1 flex items-center">
        <Users className="h-5 w-5 mr-2 text-blue-600" />
        {title}
      </h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">{subtitle}</p>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-6">{targetLabel}</p>

      {!stats.length ? (
        <div className="flex items-center justify-center h-48 text-gray-500 dark:text-gray-400">
          No worker data for the selected date range.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {stats.map((worker) => {
            const grade = gradeFromPercent(worker.fulfilledPercent);
            const gradeClass =
              grade === 'A'
                ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                : grade === 'B'
                  ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300'
                  : grade === 'C'
                    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300'
                    : 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300';
            return (
              <div
                key={worker.workerId}
                className="flex flex-col items-center p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors border border-gray-200 dark:border-gray-700"
              >
                <div className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-1">
                  {worker.fulfilledCount}
                </div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-semibold text-gray-900 dark:text-gray-100 text-center">
                    {worker.name}
                  </span>
                  <span
                    className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${gradeClass}`}
                    title="Grade by fulfilled %"
                  >
                    {grade}
                  </span>
                </div>
                {worker.email && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-full mb-3">
                    {worker.email}
                  </p>
                )}
                <div className="flex gap-6 mt-2">
                  <CircularProgress
                    value={worker.readyPercent}
                    size={80}
                    strokeWidth={8}
                    label={readyLabel}
                    count={worker.readyCount}
                  />
                  <CircularProgress
                    value={worker.fulfilledPercent}
                    size={80}
                    strokeWidth={8}
                    label={fulfilledLabel}
                    count={worker.fulfilledCount}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
